import accountApi from 'Agent/Accounts/api/accountApi';
import {AccountDetail, Discrepancy, DiscrepancySummary} from 'Agent/Accounts/models/AccountDetail';
import {ImageDto, ImageSearchResponseDto} from 'Agent/Accounts/api';
import {Async, Empty} from 'Core/models/Async';
import * as O from 'fp-ts/lib/Option';
import {Lens} from 'monocle-ts';
import {takeLatest} from 'redux-saga/effects';
import {createAction} from 'Shared/utilities/actionCreator';
import {getApiCallGenerator} from 'Shared/utilities/sagaApiGenerators';
import unionize, {ofType, UnionOf} from 'unionize';

export enum AccountDetailActionType {
  GET_ACCOUNT_DETAILS = 'account/GET_ACCOUNT_DETAILS',
  CHANGE_ACCOUNTS_DETAILS = 'account/CHANGE_ACCOUNTS_DETAILS',
  CHANGE_DISCREPANCY_SUMMARY = 'accountDetail/CHANGE_DISCREPANCY_SUMMARY',
  CHANGE_DISCREPANCIES = 'accountDetail/CHANGE_DISCREPANCIES',
  GET_USER = 'account/GET_USER',
  GET_ACCOUNT_IMAGES = 'account/GET_ACCOUNT_IMAGES',
  CHANGE_ACCOUNT_IMAGES = 'account/CHANGE_ACCOUNT_IMAGES',
}

export const changeDiscrepancies = (discrepancies: O.Option<Discrepancy[]>) =>
  createAction(AccountDetailActionType.CHANGE_DISCREPANCIES, {discrepancies});
export const getAccountDetails = (encryptedIdentifier: string, id: string, accountNumber: string) =>
  createAction(AccountDetailActionType.GET_ACCOUNT_DETAILS, {encryptedIdentifier, id, accountNumber});
export const changeDiscrepancySummary = (discrepancySummary: O.Option<DiscrepancySummary[]>) =>
  createAction(AccountDetailActionType.CHANGE_DISCREPANCY_SUMMARY, {discrepancySummary});
export const changeAccountDetails = (accountDetails: Async<AccountDetail>) =>
  createAction(AccountDetailActionType.CHANGE_ACCOUNTS_DETAILS, accountDetails);
export const getAccountImages = (encryptedIdentifier: string, accountId: string) =>
  createAction(AccountDetailActionType.GET_ACCOUNT_IMAGES, {encryptedIdentifier, accountId});
export const changeAccountImages = (accountImages: Async<AccountImages>) =>
  createAction(AccountDetailActionType.CHANGE_ACCOUNT_IMAGES, accountImages);

export type AccountImages = ImageSearchResponseDto;
export type AccountImage = ImageDto;

type AccountDetailState = {
  discrepancies: O.Option<Discrepancy[]>;
  discrepancySummaries: O.Option<DiscrepancySummary[]>;
  accountDetails: Async<AccountDetail>;
  accountImages: Async<AccountImages>;
};

export const DEFAULT_STATE: AccountDetailState = {
  discrepancies: O.none,
  accountDetails: new Empty(),
  discrepancySummaries: O.none,
  accountImages: new Empty(),
};

const AccountDetailAction = unionize(
  {
    [AccountDetailActionType.CHANGE_ACCOUNTS_DETAILS]: ofType<ReturnType<typeof changeAccountDetails>>(),
    [AccountDetailActionType.CHANGE_DISCREPANCIES]: ofType<ReturnType<typeof changeDiscrepancies>>(),
    [AccountDetailActionType.CHANGE_DISCREPANCY_SUMMARY]: ofType<ReturnType<typeof changeDiscrepancySummary>>(),
    [AccountDetailActionType.CHANGE_ACCOUNT_IMAGES]: ofType<ReturnType<typeof changeAccountImages>>(),
  },
  {
    tag: 'type',
  }
);
type AccountDetailAction = UnionOf<typeof AccountDetailAction>;
const AccountDetailsLens = Lens.fromProp<AccountDetailState>();

export default (state: AccountDetailState = DEFAULT_STATE, action: AccountDetailAction): AccountDetailState =>
  AccountDetailAction.match({
    [AccountDetailActionType.CHANGE_ACCOUNTS_DETAILS]: accountAction =>
      AccountDetailsLens('accountDetails').set(accountAction.payload)(state),
    [AccountDetailActionType.CHANGE_DISCREPANCIES]: accountAction =>
      AccountDetailsLens('discrepancies').set(accountAction.payload.discrepancies)(state),
    [AccountDetailActionType.CHANGE_DISCREPANCY_SUMMARY]: accountAction =>
      AccountDetailsLens('discrepancySummaries').set(accountAction.payload.discrepancySummary)(state),
    [AccountDetailActionType.CHANGE_ACCOUNT_IMAGES]: accountAction =>
      AccountDetailsLens('accountImages').set(accountAction.payload)(state),
    default: () => state,
  })(action);

export const getAccountDetailsFromApi = (action: ReturnType<typeof getAccountDetails>) =>
  accountApi.getAccountDetails(action.payload.encryptedIdentifier, action.payload.id, action.payload.accountNumber);

export function* getAccountImagesApi(action: ReturnType<typeof getAccountImages>) {
  yield getApiCallGenerator(
    () => accountApi.getImages(action.payload.encryptedIdentifier, action.payload.accountId),
    changeAccountImages
  )(action);
}

export function* fetchAccountImages() {
  yield takeLatest(AccountDetailActionType.GET_ACCOUNT_IMAGES, getAccountImagesApi);
}

export function* watchFetchAccountDetails() {
  yield takeLatest(
    AccountDetailActionType.GET_ACCOUNT_DETAILS,
    getApiCallGenerator(getAccountDetailsFromApi, changeAccountDetails)
  );
}
